import Link from "next/link";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { ArrowLeft } from "lucide-react";

import MaticLogo from "@assets/svg/MaticLogo";
import { useTrack } from "@/analytics/events";
import { NavMotion, seconds } from "@/blueprint/tokens/Motion";
import {
  MENU_PRIMARY_LINKS,
  MENU_RESOURCE_LINKS,
} from "@/chrome/chromeContent";
import NavShopContent from "@/chrome/SiteNav/NavShopContent";
import { NAV_WORDMARK_CLASSES } from "@/chrome/SiteNav/navWordmark";
import { cn } from "@/lib/utils";

// Figma Mobile Nav Open + Shop states.
export type NavMenuView = "menu" | "shop";

/**
 * The card's fixed internals (Figma Mobile Nav Open): a 72px header row the
 * persistent nav cluster floats over, then 48 to the links. The component's
 * own values, not the responsive scale — Figma stretches one instance from
 * 366 to 770 without re-pointing them.
 */
const HEADER_ROW_PX = 72;
const HEADER_GAP_PX = 48;

/** Content order down the sheet, which is the order the stagger follows. */
const LOGO_INDEX = 0;
const FIRST_LINK_INDEX = 1;
const SUPPORT_INDEX = FIRST_LINK_INDEX + MENU_PRIMARY_LINKS.length;

/**
 * One item arriving inside the sheet, or leaving it.
 *
 * Arrivals are ordered top to bottom and lift into place, behind the sheet
 * that has already made room for them. Departures are simultaneous and do not
 * move — the sheet is folding up behind them and a second direction of travel
 * just reads as mess. The lift is reset with a zero-duration step held until
 * after the fade, so nothing slides while it is still visible.
 */
const SheetItem = ({
  open,
  index,
  className,
  children,
}: {
  open: boolean;
  /** Position down the sheet; the stagger is read off this, top first. */
  index: number;
  className?: string;
  children: ReactNode;
}) => {
  const reducedMotion = useReducedMotion() === true;
  const { contentIn, contentOut, contentLiftPx, contentStartMs, staggerMs } =
    NavMotion;
  const delayMs = contentStartMs + index * staggerMs;

  return (
    <motion.div
      initial={false}
      /* Reduced motion zeroes the durations and the lift rather than dropping
         the motion element. `useReducedMotion` answers null on the first
         render, so a component that swaps motion.div for a plain div swaps
         them AFTER framer has written opacity:0 and a translate inline — and
         React, seeing the same tag, keeps the node and its stranded style,
         leaving the content permanently invisible. */
      animate={{
        opacity: open ? 1 : 0,
        y: reducedMotion || open ? 0 : contentLiftPx,
      }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : open
            ? {
                opacity: {
                  duration: seconds(contentIn.durationMs),
                  delay: seconds(delayMs),
                  ease: [...contentIn.ease],
                },
                y: {
                  duration: seconds(contentIn.durationMs),
                  delay: seconds(delayMs),
                  ease: [...contentIn.ease],
                },
              }
            : {
                opacity: {
                  duration: seconds(contentOut.durationMs),
                  ease: [...contentOut.ease],
                },
                y: { duration: 0, delay: seconds(contentOut.durationMs) },
              }
      }
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Keeps a view mounted so its height is always known, and slides it across the
 * sheet as the view changes.
 *
 * The sheet unfolds vertically, so the views travel horizontally: along the
 * same axis they would read as the sheet still opening. `direction` is +1
 * going forward (menu to shop) and -1 coming back, so both layers move the
 * same way and the pair reads as one strip being scrolled.
 */
const MeasuredView = ({
  active,
  direction,
  name,
  onHeightChange,
  children,
}: {
  active: boolean;
  direction: 1 | -1;
  name: NavMenuView;
  onHeightChange: (height: number) => void;
  children: ReactNode;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion() === true;
  const { viewSwitch } = NavMotion;

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (content === null) return;

    // offsetHeight, not getBoundingClientRect: the sheet is folded by a scaleY
    // while closed, and a rect read through that transform under-reports.
    const measure = () => onHeightChange(content.offsetHeight);

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const travel = viewSwitch.travelPx * direction;

  return (
    <motion.div
      ref={contentRef}
      data-nav-view={name}
      aria-hidden={!active}
      inert={!active}
      initial={false}
      animate={{ opacity: active ? 1 : 0, x: active ? 0 : travel }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : active
            ? {
                duration: seconds(viewSwitch.inDurationMs),
                delay: seconds(viewSwitch.inDelayMs),
                ease: [...NavMotion.contentIn.ease],
              }
            : {
                duration: seconds(viewSwitch.outDurationMs),
                ease: [...NavMotion.contentOut.ease],
              }
      }
      className={cn(
        "col-start-1 row-start-1 self-start",
        !active && "pointer-events-none",
      )}
    >
      {children}
    </motion.div>
  );
};

type NavMenuCardProps = {
  /** aria-controls target of the nav's menu buttons. */
  id: string;
  open: boolean;
  view: NavMenuView;
  /**
   * Figma's Open state always carries the wordmark. Pages whose nav row shows
   * a logo keep it — the row's own logo slides over this slot — so the card
   * renders one only where no counterpart exists (the homepage, the pill).
   */
  showWordmark: boolean;
  backButtonRef: Ref<HTMLButtonElement>;
  onBack: () => void;
  onNavigate: () => void;
  /** Anchoring relative to the trigger surface; supplied by the chrome. */
  className?: string;
};

const NavMenuCard = ({
  id,
  open,
  view,
  showWordmark,
  backButtonRef,
  onBack,
  onNavigate,
  className,
}: NavMenuCardProps) => {
  const track = useTrack();
  // The sheet is the only nav surface most visitors ever open, so a click here
  // has to report what was chosen, not just that the menu was opened.
  const navigateTo = (link: { href: string; label: string }, index: number) => {
    track("nav_link_clicked", {
      href: link.href,
      label: link.label,
      surface: "menu_sheet",
      nav_link_index: index,
    });
    onNavigate();
  };
  const reducedMotion = useReducedMotion() === true;

  const [viewHeights, setViewHeights] = useState<
    Partial<Record<NavMenuView, number>>
  >({});
  const setViewHeight = useCallback(
    (target: NavMenuView, height: number) =>
      setViewHeights((current) =>
        current[target] === height ? current : { ...current, [target]: height },
      ),
    [],
  );
  const onMenuHeight = useCallback(
    (height: number) => setViewHeight("menu", height),
    [setViewHeight],
  );
  const onShopHeight = useCallback(
    (height: number) => setViewHeight("shop", height),
    [setViewHeight],
  );

  // Forward is menu -> shop; the header and both views travel together.
  const direction: 1 | -1 = view === "shop" ? 1 : -1;
  // Switching views inside an open sheet is not the sheet opening: the height
  // has to land with the incoming layer, on that layer's curve, or the content
  // arrives into a box still resizing under it. Compared during render because
  // that is when framer reads the transition for the target that just changed.
  const previousViewRef = useRef<NavMenuView>(view);
  const previousOpenRef = useRef(open);
  const isViewSwitch =
    open &&
    previousOpenRef.current === open &&
    previousViewRef.current !== view;
  useLayoutEffect(() => {
    previousViewRef.current = view;
    previousOpenRef.current = open;
  }, [view, open]);

  const viewHeight = viewHeights[view];
  const cardHeight =
    viewHeight === undefined
      ? undefined
      : HEADER_ROW_PX + HEADER_GAP_PX + viewHeight;

  const showCardWordmark = view === "menu" && showWordmark;
  const { sheetOpen, surfaceClose, sheetCollapsedScaleY, viewSwitch } =
    NavMotion;

  /*
   * Shape and width are fixed: rounded-m and margin-to-margin from frame 0,
   * with only the fold and the opacity moving. Growing the box out of the
   * cluster pill while its radius was still 9999px did not read as a pill
   * becoming a sheet — border-radius clamps to half the box, so what painted
   * for the whole of the growth was an ellipse the size of the sheet, and it
   * only snapped square at the end.
   */
  const surfaceAnimate = {
    opacity: open ? 1 : 0,
    scaleY: open || reducedMotion ? 1 : sheetCollapsedScaleY,
    ...(cardHeight === undefined ? {} : { height: cardHeight }),
  };

  const surfaceTransition: Transition = reducedMotion
    ? { duration: 0 }
    : isViewSwitch
      ? {
          height: {
            duration: seconds(viewSwitch.inDurationMs),
            delay: seconds(viewSwitch.inDelayMs),
            ease: [...NavMotion.contentIn.ease],
          },
        }
      : open
        ? { duration: seconds(sheetOpen.durationMs), ease: [...sheetOpen.ease] }
        : {
            // Closing, the sheet folds only after the content has gone — the
            // delay is exactly the content's own exit.
            duration: seconds(surfaceClose.durationMs),
            delay: seconds(NavMotion.contentOut.durationMs),
            ease: [...surfaceClose.ease],
          };

  return (
    <motion.div
      id={id}
      data-theme="light"
      data-nav-surface
      aria-hidden={!open}
      inert={!open}
      initial={false}
      animate={surfaceAnimate}
      transition={surfaceTransition}
      /* Unfolds from the hamburger that opened it. */
      style={{ transformOrigin: "top right" }}
      className={cn(
        "flex flex-col gap-48 overflow-hidden rounded-m bg-surface px-20 sd:hidden",
        !open && "pointer-events-none",
        className,
      )}
    >
      {/* The slot the persistent nav cluster floats over: the sheet reserves
          the space so its own padding box matches Figma's Nav Items row, and
          fills only the left side — wordmark or the shop view's back. */}
      <div className="relative my-20 grid" style={{ height: 32 }}>
        <SheetItem
          open={open}
          index={LOGO_INDEX}
          className="col-start-1 row-start-1"
        >
          <motion.div
            aria-hidden={!showCardWordmark}
            inert={!showCardWordmark}
            initial={false}
            animate={{
              opacity: showCardWordmark ? 1 : 0,
              x: showCardWordmark ? 0 : -viewSwitch.travelPx,
            }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : {
                    duration: seconds(viewSwitch.outDurationMs),
                    ease: [...NavMotion.contentOut.ease],
                  }
            }
            className={cn(
              "flex items-center",
              !showCardWordmark && "pointer-events-none",
            )}
          >
            <Link
              href="/"
              aria-label="Matic home"
              onClick={onNavigate}
              className="text-content-primary"
            >
              <MaticLogo classes={NAV_WORDMARK_CLASSES} />
            </Link>
          </motion.div>
        </SheetItem>
        <motion.div
          aria-hidden={view !== "shop"}
          inert={view !== "shop"}
          initial={false}
          animate={{
            opacity: view === "shop" ? 1 : 0,
            x: view === "shop" ? 0 : viewSwitch.travelPx,
          }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : view === "shop"
                ? {
                    duration: seconds(viewSwitch.inDurationMs),
                    delay: seconds(viewSwitch.inDelayMs),
                    ease: [...NavMotion.contentIn.ease],
                  }
                : {
                    duration: seconds(viewSwitch.outDurationMs),
                    ease: [...NavMotion.contentOut.ease],
                  }
          }
          className={cn(
            "col-start-1 row-start-1 flex items-center",
            view !== "shop" && "pointer-events-none",
          )}
        >
          <button
            ref={backButtonRef}
            type="button"
            aria-label="Back to menu"
            onClick={onBack}
            className="relative flex size-xl items-center justify-center text-content-primary"
          >
            <ArrowLeft aria-hidden className="size-l" strokeWidth={1.15} />
          </button>
        </motion.div>
      </div>
      <div className="grid">
        <MeasuredView
          active={view === "menu"}
          direction={direction}
          name="menu"
          onHeightChange={onMenuHeight}
        >
          <div className="flex flex-col gap-l pb-20">
            <div className="flex flex-col gap-xs">
              {MENU_PRIMARY_LINKS.map((link, index) => (
                <SheetItem
                  key={link.href}
                  open={open}
                  index={FIRST_LINK_INDEX + index}
                >
                  <Link
                    href={link.href}
                    onClick={() => navigateTo(link, index)}
                    className="block font-ppMori text-nav-menu-link text-content-primary"
                  >
                    {link.label}
                  </Link>
                </SheetItem>
              ))}
            </div>
            <SheetItem open={open} index={SUPPORT_INDEX}>
              <div className="flex flex-col gap-l">
                <div className="border-t border-neutral1" />
                <div className="grid grid-cols-2 gap-x-s gap-y-s">
                  {MENU_RESOURCE_LINKS.map((link, index) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      {...(link.external === true
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      onClick={() => navigateTo(link, index)}
                      className="font-ppMori text-nav-label-sm text-content-secondary"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </SheetItem>
          </div>
        </MeasuredView>
        <MeasuredView
          active={view === "shop"}
          direction={direction}
          name="shop"
          onHeightChange={onShopHeight}
        >
          <SheetItem open={open} index={FIRST_LINK_INDEX}>
            <NavShopContent variant="sheet" onNavigate={onNavigate} />
          </SheetItem>
        </MeasuredView>
      </div>
    </motion.div>
  );
};

export default NavMenuCard;
